import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildZodSchema } from "../../services/dynamicForms/fieldSchema";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";

// actions
import { SetSendMoneyCountryCurrency } from "../../actions/sendMoney";

// types
import { RootState } from "../../types/redux";
import type {
  FieldDescriptor,
  FieldOption,
} from "../../types/components/dynamicForms";
import type { Country } from "../../types/moneyTransfer";

export const COUNTRY_FIELD = "country";
export const CURRENCY_FIELD = "currency";

interface UseCountryCurrencyFormArgs {
  countries: Country[];
  countryOptions: FieldOption[];
}

const buildCountryCurrencyFields = (
  countryOptions: FieldOption[]
): FieldDescriptor[] => [
    { name: COUNTRY_FIELD, kind: "select", label: "Country", constraints: { required: true }, options: countryOptions },
    { name: CURRENCY_FIELD, kind: "select", label: "Currency", constraints: { required: true }, options: [] },
  ];

const toCurrencyOptions = (
  countries: Country[],
  iso: string
): FieldOption[] => {
  const country = countries.find((c) => c.iso === iso);
  return (country?.currencies ?? []).map((cur) => ({
    label: cur.name,
    value: cur.iso,
  }));
};

export const useCountryCurrencyForm = ({
  countries,
  countryOptions,
}: UseCountryCurrencyFormArgs) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const goingForward = useRef(false);

  const selectedCountry = useSelector(
    (state: RootState) => state.sendMoney.selectedCountry
  );
  const selectedCurrency = useSelector(
    (state: RootState) => state.sendMoney.selectedCurrency
  );
  const fields = useMemo<FieldDescriptor[]>(
    () => buildCountryCurrencyFields(countryOptions),
    [countryOptions]
  );
  const isSingleCountry = countryOptions.length === 1;

  const schema = useMemo(() => buildZodSchema(fields), [fields]);

  const form = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: { [COUNTRY_FIELD]: selectedCountry ?? "", [CURRENCY_FIELD]: selectedCurrency ?? "" },
  });

  const { watch, setValue, getValues, handleSubmit } = form;

  // Single corridor: select it for the user. Runs once the countries load
  // (the form is created before they arrive), and overrides a stale restored
  // value that isn't the one available country.
  useEffect(() => {
    if (!isSingleCountry) return;
    const only = countryOptions[0].value;
    if (getValues(COUNTRY_FIELD) === only) return;
    setValue(COUNTRY_FIELD, only, { shouldValidate: true });
  }, [isSingleCountry, countryOptions, getValues, setValue]);

  const selectedCountryIso = watch(COUNTRY_FIELD) as string;

  const currencyOptions = useMemo<FieldOption[]>(
    () => toCurrencyOptions(countries, selectedCountryIso),
    [countries, selectedCountryIso]
  );

  // Auto-default the currency to the first option, but ONLY when the current
  // value isn't already valid for the selected country. Two cases depend on
  // this guard:
  //   1. Restore (back-nav / refresh): defaultValues seeds currency from
  //      persisted state. This effect fires because country goes "" -> "PH";
  //      without the guard it would clobber the restored currency with the
  //      first option. The guard sees the restored value is valid and bails.
  //   2. Genuine country change (PH -> US): the old currency (PHP) is NOT in
  //      the new country's options, so stillValid is false and we reset to the
  //      first option, as intended.
  useEffect(() => {
    const current = form.getValues(CURRENCY_FIELD) as string;
    const stillValid = currencyOptions.some((o) => o.value === current);
    if (stillValid) return; // keep restored / valid selection
    setValue(CURRENCY_FIELD, currencyOptions[0]?.value ?? "", {
      shouldValidate: currencyOptions.length > 0,
    });
  }, [currencyOptions, setValue, form]);

  // inject live currency options; disable currency until a country is chosen;
  // lock country when it's the only corridor
  const renderFields = useMemo<FieldDescriptor[]>(
    () =>
      fields.map((f) => {
        if (f.name === CURRENCY_FIELD) {
          return { ...f, options: currencyOptions, readOnly: !selectedCountryIso };
        }
        if (f.name === COUNTRY_FIELD) {
          return { ...f, readOnly: isSingleCountry };
        }
        return f;
      }),
    [fields, currencyOptions, selectedCountryIso, isSingleCountry]
  );

  // Persist the selected country and currency to the store. This is called on submit, back, and unmount (if not going forward).
  const persist = (country: string, currency: string) => {
    dispatch(SetSendMoneyCountryCurrency({ country, currency }));
  };

  /**
   * Handle form submission. If the form is valid, dispatch the selected country and currency to the store and navigate to the transfer details page.
   */
  const submit = handleSubmit((values) => {
    goingForward.current = true; // forward nav: keep state
    persist(values[COUNTRY_FIELD] as string, values[CURRENCY_FIELD] as string);
    history.push(ROUTES.SEND_MONEY.TRANSFER_DETAILS);
  });

  const goBack = () => {
    persist("", ""); // leaving the flow backward: clear
    history.goBack();
  };

  // Abandonment: unmount without going forward = clear. Forward nav keeps state
  // so a back-nav from TransferDetails restores the selection.
  useEffect(() => {
    return () => {
      if (!goingForward.current) {
        persist("", "");
      }
    };
  }, [dispatch]);

  return { form, renderFields, submit, goBack, selectedCountryIso };
};